import { Info } from "lucide-react";
import { useRef, useState } from "react";
import type { EconHeaderCountdownEvent } from "../../types/econ-header";
import type { IVScoreResponse } from "../../types/market-data";
import { MobileIvScoringDrawer } from "../MobileIvScoringDrawer";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import {
  LATEST_ECON_PRINT_DRAWER_ID,
  LatestEconPrintDrawer,
} from "./LatestEconPrintDrawer";
import type { IvScoreStyle } from "./iv-score-visuals";

interface IVScoreMobileChipProps {
  capLightStyle: IvScoreStyle;
  data: IVScoreResponse;
  flash: boolean;
  scoreColor: string;
  valueStyle: IvScoreStyle;
  latestReleasedEconEvent?: EconHeaderCountdownEvent | null;
}

export function IVScoreMobileChip(props: IVScoreMobileChipProps) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [latestPrintOpen, setLatestPrintOpen] = useState(false);
  const points = props.data.points;
  const opensLatestPrint = Boolean(props.latestReleasedEconEvent);

  return (
    <>
      <button
        ref={(node) => {
          anchorRef.current = node;
        }}
        type="button"
        className="fintheon-iv-score-card fintheon-black-liquid-glass fintheon-glass-pill mobile-iv-header-glass header-widget-glass solvys-liquid-glass relative flex h-[34px] w-[min(226px,calc(100vw-116px))] min-w-0 items-center justify-center gap-1.5 rounded-full border border-transparent px-2.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
        style={props.capLightStyle}
        aria-label={
          opensLatestPrint
            ? "Open today's latest released Desk Plan event"
            : "Open IV and TiltScout breakdown"
        }
        aria-controls={
          opensLatestPrint
            ? LATEST_ECON_PRINT_DRAWER_ID
            : "mobile-iv-scoring-drawer"
        }
        aria-expanded={opensLatestPrint ? latestPrintOpen : scoringOpen}
        onClick={() =>
          opensLatestPrint
            ? setLatestPrintOpen(true)
            : setScoringOpen(true)
        }
      >
        <span className="flex min-w-0 items-baseline gap-1">
          <span className="text-[9px] uppercase tracking-[0.16em] text-gray-500">
            VIX
          </span>
          <span
            className={`font-mono text-[11px] text-gray-300 ${props.flash ? "animate-[vix-value-flash_150ms_ease-in-out]" : ""}`}
            style={{
              ...props.valueStyle,
              color: "var(--iv-score-severity-color)",
            }}
          >
            <FintheonNumericText text={props.data.vix.level.toFixed(2)} />
          </span>
        </span>
        <span className="mobile-iv-column-divider" aria-hidden="true" />
        <span className="flex min-w-0 items-baseline gap-1">
          <span className="text-[9px] uppercase tracking-[0.16em] text-gray-500">
            IV
          </span>
          <span
            className="font-mono text-[14px] font-bold"
            style={{ color: props.scoreColor }}
          >
            <FintheonNumericText
              kind="score"
              text={props.data.score.toFixed(1)}
            />
          </span>
        </span>
        <span className="mobile-iv-column-divider" aria-hidden="true" />
        <span className="flex min-w-0 items-baseline gap-1 font-mono text-[10px]">
          <span className="font-medium text-[var(--fintheon-text)]/42">
            {props.data.instrument}
          </span>
          {points ? (
            <span className="font-medium text-[var(--fintheon-accent)]">
              ±<FintheonNumericText text={points.scaledPoints} />
            </span>
          ) : null}
        </span>
        <Info className="ml-0.5 h-2.5 w-2.5 text-gray-500" aria-hidden="true" />
      </button>
      <MobileIvScoringDrawer
        data={props.data}
        open={scoringOpen}
        onClose={() => setScoringOpen(false)}
      />
      <LatestEconPrintDrawer
        anchorRef={anchorRef}
        open={latestPrintOpen}
        onClose={() => setLatestPrintOpen(false)}
        latestReleasedEvent={props.latestReleasedEconEvent}
      />
    </>
  );
}
